import React, { useMemo, useState } from 'react';
import { transfers } from '../data/transfers';
import { clubs, getClubById } from '../data/clubs';

const StatsPanel = ({ selectedSellerLeague, selectedBuyerLeague, minAmount, maxAmount, groupingMode, excludeOtherLeagues }) => {
  const [showAllStats, setShowAllStats] = useState(false);
  const filteredTransfers = useMemo(() => {
    return transfers.filter(transfer => {
      if (transfer.season !== "2025/26") return false;
      
      const fromClub = getClubById(transfer.fromClubId);
      const toClub = getClubById(transfer.toClubId);
      
             if (selectedSellerLeague !== 'All' && fromClub?.league !== selectedSellerLeague) return false;
       if (selectedBuyerLeague !== 'All' && toClub?.league !== selectedBuyerLeague) return false;
       if (transfer.amount < minAmount || transfer.amount > maxAmount) return false;
       
       // Exclude other leagues filter
       if (excludeOtherLeagues) {
         const majorLeagues = ['Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1'];
         if (!majorLeagues.includes(fromClub?.league) && !majorLeagues.includes(toClub?.league)) {
           return false;
         }
       }
       
       return true;
    });
     }, [selectedSellerLeague, selectedBuyerLeague, minAmount, maxAmount, excludeOtherLeagues]);

  const stats = useMemo(() => {
    if (filteredTransfers.length === 0) {
      return {
        totalTransfers: 0,
        totalAmount: 0,
        averageAmount: 0,
        highestTransfer: null,
        uniqueClubs: 0,
        topSpendingClub: null,
        uniqueLeagues: 0,
        topSpendingLeague: null
      };
    }

    const totalAmount = filteredTransfers.reduce((sum, transfer) => sum + transfer.amount, 0);
    const averageAmount = totalAmount / filteredTransfers.length;
    const highestTransfer = filteredTransfers.reduce((max, transfer) => 
      transfer.amount > max.amount ? transfer : max
    );

    // Get unique clubs
    const uniqueClubIds = new Set();
    filteredTransfers.forEach(transfer => {
      uniqueClubIds.add(transfer.fromClubId);
      uniqueClubIds.add(transfer.toClubId);
    });

    // Calculate club spending
    const clubSpending = {};
    filteredTransfers.forEach(transfer => {
      const toClub = getClubById(transfer.toClubId);
      if (toClub) {
        clubSpending[toClub.name] = (clubSpending[toClub.name] || 0) + transfer.amount;
      }
    });

    const topSpendingClub = Object.entries(clubSpending).reduce((max, [club, amount]) => 
      amount > max.amount ? { club, amount } : max
    , { club: '', amount: 0 });

    // Get unique leagues
    const uniqueLeagues = new Set();
    filteredTransfers.forEach(transfer => {
      const fromClub = getClubById(transfer.fromClubId);
      const toClub = getClubById(transfer.toClubId);
      if (fromClub) uniqueLeagues.add(fromClub.league);
      if (toClub) uniqueLeagues.add(toClub.league);
    });

    // Calculate league spending
    const leagueSpending = {};
    filteredTransfers.forEach(transfer => {
      const toClub = getClubById(transfer.toClubId);
      if (toClub) {
        leagueSpending[toClub.league] = (leagueSpending[toClub.league] || 0) + transfer.amount;
      }
    });

    const topSpendingLeague = Object.entries(leagueSpending).reduce((max, [league, amount]) => 
      amount > max.amount ? { league, amount } : max
    , { league: '', amount: 0 });

    return {
      totalTransfers: filteredTransfers.length,
      totalAmount,
      averageAmount,
      highestTransfer,
      uniqueClubs: uniqueClubIds.size,
      topSpendingClub,
      uniqueLeagues: uniqueLeagues.size,
      topSpendingLeague
    };
  }, [filteredTransfers]);

  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    }
    return `€${(amount / 1000).toFixed(0)}K`;
  };

  if (stats.totalTransfers === 0) {
    return (
      <div className="stats-panel">
        <div className="stat-item">
          <span className="stat-label">No transfers found</span>
          <span className="stat-value">-</span>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-panel">
      {/* Always show key stats */}
      <div className="stat-item">
        <span className="stat-label">Total Transfers</span>
        <span className="stat-value">{stats.totalTransfers}</span>
      </div>
      
      <div className="stat-item">
        <span className="stat-label">Total Amount</span>
        <span className="stat-value">{formatAmount(stats.totalAmount)}</span>
      </div>
      
      <div className="stat-item">
        <span className="stat-label">Average Transfer</span>
        <span className="stat-value">{formatAmount(stats.averageAmount)}</span>
      </div>
      
      <div className="stat-item">
        <span className="stat-label">Highest Transfer</span>
        <div className="stat-value">
          <div>{formatAmount(stats.highestTransfer.amount)}</div>
          <div className="stat-subvalue">{stats.highestTransfer.player}</div>
        </div>
      </div>
      
      {/* Show expand button if there are more stats */}
      {!showAllStats && (
        <button 
          className="expand-stats-button"
          onClick={() => setShowAllStats(true)}
          style={{
            marginTop: '10px',
            padding: '6px 12px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '6px',
            color: 'white',
            fontSize: '11px',
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Show More Stats
        </button>
      )}
      
      {/* Additional stats - only show when expanded */}
      {showAllStats && (
        <>
          {groupingMode === 'none' && (
            <>
              <div className="stat-item">
                <span className="stat-label">Unique Clubs</span>
                <span className="stat-value">{stats.uniqueClubs}</span>
              </div>
              
              <div className="stat-item">
                <span className="stat-label">Top Spending Club</span>
                <div className="stat-value">
                  <div>{stats.topSpendingClub.club}</div>
                  <div className="stat-subvalue">{formatAmount(stats.topSpendingClub.amount)}</div>
                </div>
              </div>
            </>
          )}
          
          {groupingMode !== 'none' && (
            <>
              <div className="stat-item">
                <span className="stat-label">Unique Leagues</span>
                <span className="stat-value">{stats.uniqueLeagues}</span>
              </div>
              
              <div className="stat-item">
                <span className="stat-label">Top Spending League</span>
                <div className="stat-value">
                  <div>{stats.topSpendingLeague.league}</div>
                  <div className="stat-subvalue">{formatAmount(stats.topSpendingLeague.amount)}</div>
                </div>
              </div>
            </>
          )}
          
          <button 
            className="collapse-stats-button"
            onClick={() => setShowAllStats(false)}
            style={{
              marginTop: '10px',
              padding: '6px 12px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '11px',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Show Less
          </button>
        </>
      )}
    </div>
  );
};

export default StatsPanel;

